import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { combineLatest, debounceTime, distinctUntilChanged, filter, startWith, tap } from 'rxjs';
import { SmallNumberPipe } from 'src/app/pipes/small-number/small-number.pipe';
import { AuthService } from 'src/app/services/auth-service/auth-service.service';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { TopItemsService } from 'src/app/services/top-items/top-items.service';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { LOCATIONS, getAllMarketLocations } from 'src/app/shared/constants/locations.constant';
import { Location } from 'src/app/shared/models/location.model';
import { TopItem } from 'src/app/shared/models/top-items.model';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { CopyTextButtonComponent } from 'src/app/shared/components/copy-text-button/copy-text-button.component';
import { CheckPriceButtonComponent } from 'src/app/shared/components/check-price-button/check-price-button.component';
import { MatInputModule } from '@angular/material/input';
import { ShopCategory } from 'src/app/shared/models/items.model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ItemImageComponent } from 'src/app/shared/components/item-image/item-image.component';
import { sign } from 'node:crypto';
import { LoadingBarService } from 'src/app/services/loading-bar/loading-bar.service';
import { CategoriesMenuComponent } from 'src/app/shared/components/categories-menu/categories-menu.component';

@Component({
  selector: 'app-top-items',
  imports: [
    CommonModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatExpansionModule,
    FormsModule,
    MatSelectModule,
    MatFormFieldModule,
    NgxMatSelectSearchModule,
    ReactiveFormsModule,
    SmallNumberPipe,
    MatCardModule,
    MatDatepickerModule,
    CopyTextButtonComponent,
    CheckPriceButtonComponent,
    MatInputModule,
    MatTooltipModule,
    MatPaginatorModule,
    MatProgressBarModule,
    ItemImageComponent,
    CategoriesMenuComponent,
  ],
  templateUrl: './top-items.component.html',
  styleUrl: './top-items.component.scss',
  providers: [provideNativeDateAdapter(), { provide: MAT_DATE_LOCALE, useValue: navigator.language }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopItemsComponent implements OnInit, AfterViewInit {
  @ViewChild('paginator') paginator!: MatPaginator;
  topItems: TopItem[] = [];
  dataSource: MatTableDataSource<TopItem> = new MatTableDataSource<TopItem>(this.topItems);
  locations: Location[] = getAllMarketLocations();
  startDate = new Date(new Date().setDate(new Date().getDate() - 5));
  endDate = new Date(new Date().setDate(new Date().getDate() - 3));
  filterFormGroup = new FormGroup({
    locationForm: new FormControl<Location>(LOCATIONS.BlackMarket),
    startDateForm: new FormControl<Date | null>(this.startDate),
    endDateForm: new FormControl<Date | null>(this.endDate),
    filterInput: new FormControl<string>(''),
    sortBy: new FormControl<'amount' | 'silver'>('silver'),
  });
  selectedCategories = signal<Set<ShopCategory>>(new Set<ShopCategory>());
  get formText(): string {
    return (
      'Top Traded Items for ' +
      (this.filterFormGroup.get('locationForm')?.value?.displayName ?? '') +
      ' from ' +
      (this.filterFormGroup.get('startDateForm')?.value?.toLocaleDateString(navigator.language) ?? '') +
      ' to ' +
      (this.filterFormGroup.get('endDateForm')?.value?.toLocaleDateString(navigator.language) ?? '')
    );
  }

  sortOptions = [
    { value: 'amount', displayName: 'Amount' },
    { value: 'silver', displayName: 'Silver' },
  ];

  constructor(
    public userSettingsService: UserSettingsService,
    public authService: AuthService,
    public jsonReaderService: JsonReaderService,
    public topItemsService: TopItemsService,
    public loadingBarService: LoadingBarService
  ) {}

  async ngOnInit() {
    this.jsonReaderService.ensureLoaded();

    // updating top items when user/server/jsonreaderhasloaded changes
    combineLatest([this.authService.user$.pipe(distinctUntilChanged()), this.jsonReaderService.hasLoaded$.pipe(distinctUntilChanged()), this.userSettingsService.userServer$.pipe(distinctUntilChanged())])
      .pipe(
        tap(() => {
          this.topItems = [];
          this.setupDataSource();
        }),
        debounceTime(1000),
        filter(([user, hasLoaded, server]) => user != null && hasLoaded === true && server != null)
      )
      .subscribe(async ([user, hasLoaded, server]) => {
        this.updateTopItems();
      });

    // applying filter when filter changes
    this.filterFormGroup.controls.filterInput.valueChanges.pipe(debounceTime(250)).subscribe((value) => {
      this.applyFilter(value);
    });

    combineLatest([
      this.filterFormGroup.controls.locationForm.valueChanges.pipe(startWith(this.filterFormGroup.controls.locationForm.value)).pipe(distinctUntilChanged()),
      this.filterFormGroup.controls.startDateForm.valueChanges.pipe(startWith(this.filterFormGroup.controls.startDateForm.value)).pipe(distinctUntilChanged()),
      this.filterFormGroup.controls.endDateForm.valueChanges.pipe(startWith(this.filterFormGroup.controls.endDateForm.value)).pipe(distinctUntilChanged()),
      this.filterFormGroup.controls.sortBy.valueChanges.pipe(startWith(this.filterFormGroup.controls.sortBy.value)).pipe(distinctUntilChanged()),
    ]).subscribe(() => {
      this.topItems = [];
      this.setupDataSource();
    });
  }

  ngAfterViewInit() {
    // initial setup
    this.setupDataSource();
  }

  updateTopItems() {
    this.topItems = [];
    var location = this.filterFormGroup.get('locationForm')?.value ?? LOCATIONS.BlackMarket;
    var server = this.userSettingsService.getUserServer();
    var startDate = this.filterFormGroup.get('startDateForm')?.value;
    var endDate = this.filterFormGroup.get('endDateForm')?.value;
    var shopCatSubCats = Array.from(this.selectedCategories())
      .map((x) => x.shopCatSubCat())
      .join(',');
    var sortBy = this.filterFormGroup.get('sortBy')?.value ?? 'silver';
    this.topItemsService.getTopItems(server, location, 100, sortBy, shopCatSubCats, startDate ?? null, endDate ?? null).subscribe((topItems) => {
      this.topItems = topItems;
      this.setupDataSource();
    });
  }

  applyFilter(value: string | null) {
    const filterValue = value ? value : '';
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  setupDataSource() {
    this.dataSource = new MatTableDataSource<TopItem>(this.topItems);
    this.dataSource.paginator = this.paginator;
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      return data.item.localizedName.toLowerCase().includes(filter);
    };
    this.applyFilter(this.filterFormGroup.controls.filterInput.value);
  }

  getCountString(control: FormControl, max: number): string {
    if (!control.value || control.value.length === 0) {
      return '(All)';
    }
    return `(${control.value.length}/${max})`;
  }
}
